import { useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { localDateString } from '../../../../utils/helpers/localDate';
import { typeMeta } from './investmentTypes';

// Record today's market value of a holding (mutual fund NAV x units, share
// price, bond market value ...). Books are not touched - the investment stays
// at cost in the ledger; this is the valuation shown on the portfolio.
const InvestmentValuationModal = ({ investment, onClose, onSaved }) => {
    const societyId = selectedSocietyStorer.getSocietyId();
    const [value, setValue] = useState(investment.current_value != null ? String(parseFloat(investment.current_value)) : '');
    const [date, setDate] = useState(localDateString());
    const [units, setUnits] = useState(investment.units != null ? String(parseFloat(investment.units)) : '');
    const [saving, setSaving] = useState(false);

    const invested = parseFloat(investment.principal_amount) || 0;
    const current = parseFloat(value);
    const gain = Number.isFinite(current) ? current - invested : null;

    const handleSave = async () => {
        if (!Number.isFinite(current) || current < 0) {
            toast.warn('Enter the current value');
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.patch(
                apiEndpoints.UPDATE_INVESTMENT_VALUATION_API.replace(':societyId', societyId).replace(':id', investment.id),
                { current_value: current, current_value_date: date, units: units === '' ? null : parseFloat(units) }
            );
            toast.success('Current value updated');
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to update value');
        } finally {
            setSaving(false);
        }
    };

    const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500';
    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div>
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Update Current Value</h2>
                        <p className="tw-text-[11px] tw-text-gray-500">{typeMeta(investment.investment_type).short} · {investment.investment_number}</p>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>
                <div className="tw-p-5 tw-space-y-3">
                    <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1">Current Value (₹) <span className="tw-text-red-500">*</span></label>
                            <input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} className={inputClass} autoFocus />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1">Value as on <span className="tw-text-red-500">*</span></label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1">Units / Quantity</label>
                            <input type="number" min="0" step="0.0001" value={units} onChange={(e) => setUnits(e.target.value)} className={inputClass} />
                        </div>
                        <div className="tw-bg-gray-50 tw-rounded-md tw-px-3 tw-py-2">
                            <p className="tw-text-[10px] tw-text-gray-500">Invested ₹{invested.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                            {gain != null && (
                                <p className={`tw-text-xs tw-font-semibold ${gain >= 0 ? 'tw-text-emerald-600' : 'tw-text-rose-600'}`}>
                                    {gain >= 0 ? 'Gain' : 'Loss'} ₹{Math.abs(gain).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </p>
                            )}
                        </div>
                    </div>
                    <p className="tw-text-[11px] tw-text-gray-500">
                        For the portfolio view only — the ledger keeps the investment at cost. Gains are booked when it is redeemed.
                    </p>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                    <button onClick={onClose} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-50">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50">
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvestmentValuationModal;
